import { useState, type PointerEvent } from 'react';
import { Alert, Button, Card, Icon, Table, cn, type Column } from '@evogard/ui';
import type { ManagementStates } from '@evogard/contract';
import {
  BRAZILIAN_STATE_INFO,
  count,
  type BrazilianState,
  type ClaimFlowPeriod,
} from '@evogard/domain';
import { BRAZIL_MAP_SIZE, BRAZIL_MAP_STATES } from '../../../components/map/brazilStates.js';
import { useManagementStates } from './useManagement.js';

/**
 * Os sinistros por estado (26/10/2026): o mapa do Brasil do Painel da Gestão do sinistro,
 * pela UF do local do ocorrido.
 *
 * Coroplético de uma cor só, do claro ao escuro: quanto mais escuro, mais
 * sinistros. As faixas são cinco partes iguais de 1 até o maior estado, e o
 * estado sem sinistro fica sem cor — zero não é "pouco". A lista ao lado dá o
 * número exato e a ordem, que a cor sozinha não dá; e quem não tem local do
 * ocorrido é contado embaixo, em vez de sumir do mapa.
 *
 * O desenho dos estados é gerado da malha do IBGE (`tools/geo/brazil-states.mjs`).
 */

const STEPS = 5;
/* A opacidade da cor em cada faixa: o mesmo laranja, do mais claro ao cheio. */
const STEP_OPACITY = [0.22, 0.4, 0.58, 0.78, 1] as const;

const share = (n: number, total: number) => (total ? Math.round((n / total) * 100) : 0);

/** A faixa de 0 a 4, ou `null` para o estado sem sinistro. */
function stepOf(n: number, max: number): number | null {
  if (n <= 0) return null;
  if (max <= 1) return STEPS - 1;
  return Math.min(STEPS - 1, Math.floor(((n - 1) / (max - 1)) * STEPS));
}

/** O intervalo de cada faixa, para a legenda: "1–3", "4–6"… */
function stepRanges(max: number): string[] {
  if (max <= 1) return ['1'];
  return Array.from({ length: STEPS }, (_, i) => {
    const lo = Math.ceil(1 + ((max - 1) * i) / STEPS);
    const hi = i === STEPS - 1 ? max : Math.ceil(1 + ((max - 1) * (i + 1)) / STEPS) - 1;
    return lo >= hi ? `${lo}` : `${lo}–${hi}`;
  }).filter((r, i, all) => all.indexOf(r) === i);
}

export function ClaimStatesMap({ days }: { days: ClaimFlowPeriod }) {
  const [asTable, setAsTable] = useState(false);
  const query = useManagementStates(days);
  const data = query.data;
  const located = data ? data.states.reduce((n, s) => n + s.total, 0) : 0;

  return (
    <Card
      as="figure"
      className="@container"
      title="Sinistros por estado"
      descricao="Pela UF do local do ocorrido, só o sinistro do associado — o do terceiro é o mesmo acidente."
      actions={
        located > 0 &&
        !query.isError && (
          <Button variant="discreto" size="sm" onClick={() => setAsTable((v) => !v)}>
            <Icon nome={asTable ? 'grafico' : 'grade'} size={16} />
            {asTable ? 'Ver mapa' : 'Ver tabela'}
          </Button>
        )
      }
    >
      {query.isError ? (
        <Alert tone="erro" title="Não conseguimos carregar o mapa">
          Nada foi desenhado, para não mostrar número velho como se fosse atual.
          <span className="mt-1 block font-mono text-xs">{query.error.message}</span>
        </Alert>
      ) : !data ? (
        <div
          className="h-80 animate-pulse rounded-borda bg-superficie-suave"
          aria-busy="true"
        />
      ) : data.total === 0 ? (
        <p className="py-10 text-center text-sm text-texto-suave">
          Nenhum sinistro aberto nos últimos {days} dias. Quando houver, o mapa mostra em
          que estado cada um aconteceu.
        </p>
      ) : asTable ? (
        <StatesTable data={data} located={located} />
      ) : (
        <div className="grid items-start gap-5 @lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
          <StatesFigure states={data.states} located={located} noun={CLAIM_NOUN} />
          <Ranking states={data.states} located={located} noun={CLAIM_NOUN} />
        </div>
      )}
      {data && data.total > 0 && (
        <figcaption className="mt-3 flex flex-wrap items-center gap-x-1 text-xs text-texto-suave">
          {count(located, 'sinistro de associado está', 'sinistros de associado estão')}{' '}
          no mapa, de {data.total} abertos no período.
          {data.withThirdParty > 0 && (
            <span className="inline-flex items-center gap-1">
              <Icon nome="terceiro" size={16} />
              {count(data.withThirdParty, 'envolve terceiro.', 'envolvem terceiro.')}
            </span>
          )}
          {data.withoutLocation > 0 &&
            ` ${count(data.withoutLocation, 'não tem', 'não têm')} local do ocorrido gravado.`}
          {data.unknownState > 0 &&
            ` ${count(data.unknownState, 'tem', 'têm')} um estado que não é UF, gravado antes de a lista valer.`}
        </figcaption>
      )}
    </Card>
  );
}

/** Um estado no mapa; o terceiro só existe no sinistro. */
export interface StateCount {
  state: BrazilianState;
  total: number;
  withThirdParty?: number;
}

/** O que o mapa conta, no singular e no plural. */
export type StateNoun = [string, string];
const CLAIM_NOUN: StateNoun = ['sinistro', 'sinistros'];

/**
 * O mapa, com a legenda das faixas e a dica ao passar o mouse. Serve ao
 * sinistro e a Periféricos (28/10/2026): quem chama diz o que conta.
 */
export function StatesFigure({
  states,
  located,
  noun,
}: {
  states: readonly StateCount[];
  located: number;
  noun: StateNoun;
}) {
  const [tip, setTip] = useState<{ x: number; y: number; lines: string[] } | null>(null);
  const byState = new Map(states.map((s) => [s.state, s.total]));
  const thirdOf = new Map(states.map((s) => [s.state, s.withThirdParty ?? 0]));
  const max = Math.max(1, ...states.map((s) => s.total));
  const ranges = stepRanges(max);
  const show = (e: PointerEvent, uf: BrazilianState) => {
    const box = (e.currentTarget as Element)
      .closest('[data-states]')!
      .getBoundingClientRect();
    const n = byState.get(uf) ?? 0;
    setTip({
      x: e.clientX - box.left,
      y: e.clientY - box.top,
      lines: [
        `${BRAZILIAN_STATE_INFO[uf].name} (${uf})`,
        n
          ? `${count(n, noun[0], noun[1])} · ${share(n, located)}% dos que têm local`
          : `nenhum ${noun[0]} no período`,
        ...(thirdOf.get(uf)
          ? [count(thirdOf.get(uf)!, 'envolve terceiro', 'envolvem terceiro')]
          : []),
      ],
    });
  };

  return (
    <div className="relative" data-states onPointerLeave={() => setTip(null)}>
      <svg
        viewBox={`0 0 ${BRAZIL_MAP_SIZE.width} ${BRAZIL_MAP_SIZE.height}`}
        className="mx-auto block w-full max-w-[21rem]"
        role="img"
        aria-label={`Mapa do Brasil com os ${noun[1]} por estado: ${states
          .map((s) => `${BRAZILIAN_STATE_INFO[s.state].name}, ${s.total}`)
          .join('; ')}.`}
      >
        {BRAZIL_MAP_STATES.map((s) => {
          const step = stepOf(byState.get(s.uf) ?? 0, max);
          return (
            <path
              key={s.uf}
              d={s.d}
              fillRule="evenodd"
              strokeWidth={1}
              strokeLinejoin="round"
              className={cn(
                step === null
                  ? 'fill-superficie-suave stroke-borda'
                  : 'fill-primaria stroke-superficie hover:opacity-80',
              )}
              style={step === null ? undefined : { fillOpacity: STEP_OPACITY[step] }}
              onPointerMove={(e) => show(e, s.uf)}
            />
          );
        })}
        {/* A sigla só onde há sinistro: no estado vazio, ela seria ruído. */}
        {BRAZIL_MAP_STATES.map((s) =>
          byState.get(s.uf) ? (
            <text
              key={s.uf}
              x={s.x}
              y={s.y + 4}
              textAnchor="middle"
              className={cn(
                'pointer-events-none text-[13px] font-semibold',
                (stepOf(byState.get(s.uf)!, max) ?? 0) >= 3
                  ? 'fill-contraste'
                  : 'fill-texto',
              )}
            >
              {s.uf}
            </text>
          ) : null,
        )}
      </svg>
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-texto-suave">
        <span className="flex items-center gap-1.5">
          <i
            aria-hidden
            className="size-3 rounded-[3px] border border-borda bg-superficie-suave"
          />
          nenhum
        </span>
        {ranges.map((r, i) => (
          <span key={r} className="flex items-center gap-1.5">
            <i
              aria-hidden
              className="size-3 rounded-[3px] bg-primaria"
              style={{
                opacity:
                  STEP_OPACITY[ranges.length === 1 ? STEPS - 1 : i] ?? STEP_OPACITY[0],
              }}
            />
            {r}
          </span>
        ))}
      </div>
      {tip && (
        <div
          role="presentation"
          className="pointer-events-none absolute z-10 rounded-borda border border-borda bg-superficie px-3 py-2 text-xs shadow-flutuante"
          style={{ left: tip.x + 12, top: tip.y + 12 }}
        >
          {tip.lines.map((line, i) => (
            <p
              key={i}
              className={i === 0 ? 'font-semibold text-texto' : 'text-texto-suave'}
            >
              {line}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

/** A ordem e o número exato: os estados com algum, do maior ao menor. */
export function Ranking({
  states,
  located,
  noun,
}: {
  states: readonly StateCount[];
  located: number;
  noun: StateNoun;
}) {
  const max = Math.max(1, ...states.map((s) => s.total));
  return (
    <ol className="flex flex-col gap-2.5" aria-label={`Estados com mais ${noun[1]}`}>
      {states.map((s) => (
        <li
          key={s.state}
          className="grid grid-cols-[minmax(0,9rem)_minmax(0,1fr)_auto] items-center gap-3 text-sm"
        >
          <span className="truncate text-texto">
            {BRAZILIAN_STATE_INFO[s.state].name}
          </span>
          <span className="h-2 overflow-hidden rounded-pilula bg-superficie-suave">
            <span
              className="block h-full rounded-pilula bg-primaria"
              style={{ width: `${(s.total / max) * 100}%` }}
            />
          </span>
          <span className="text-right text-xs text-texto-suave tabular-nums">
            <b className="font-semibold text-texto">{s.total}</b> ·{' '}
            {share(s.total, located)}%
            {!!s.withThirdParty && (
              <span
                className="ml-2 inline-flex items-center gap-1"
                title={count(s.withThirdParty, 'envolve terceiro', 'envolvem terceiro')}
              >
                <Icon nome="terceiro" size={16} />
                {s.withThirdParty}
                <span className="sr-only"> envolvendo terceiro</span>
              </span>
            )}
          </span>
        </li>
      ))}
    </ol>
  );
}

interface StateRow {
  state: BrazilianState;
  total: number;
  withThirdParty: number;
}

function StatesTable({ data, located }: { data: ManagementStates; located: number }) {
  const columns: Column<StateRow>[] = [
    {
      title: 'Estado',
      cell: (r) => `${BRAZILIAN_STATE_INFO[r.state].name} (${r.state})`,
      alignment: 'inicio',
    },
    { title: 'Sinistros', cell: (r) => r.total, alignment: 'fim' },
    {
      title: '% dos que têm local',
      cell: (r) => `${share(r.total, located)}%`,
      alignment: 'fim',
    },
    { title: 'Envolvem terceiro', cell: (r) => r.withThirdParty, alignment: 'fim' },
  ];
  return (
    <Table
      columns={columns}
      items={data.states}
      rowKey={(r) => r.state}
      label={`Sinistros por estado nos últimos ${data.days} dias`}
      empty="Nenhum sinistro com local do ocorrido no período."
    />
  );
}
